import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { isExternalGame, getExternalGameStyling } from '@/services/externalGameService';
import { isImageIconUrl } from '@/utils/iconRenderer';
import type { Game } from '@/services/api-client';

interface GameCardProps {
  game: Game | null;
  to?: string;
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  showHeader?: boolean; // Controls whether to show the game header with icon, title, tagline
  showTitleOnly?: boolean; // Controls whether to show only the game title (no icon, no tagline)
  onCardClick?: () => void; // D39: card-zone click handler (logo, title, body → today's game)
}

export function GameCard({
  game,
  to,
  className,
  children,
  isLoading = false,
  showHeader = true, // Default to showing header to maintain backward compatibility
  showTitleOnly = false,
  onCardClick,
}: GameCardProps) {
  // Loading skeleton
  if (isLoading || !game) {
    return (
      <div
        className={cn(
          'relative bg-[#f8f8f8] dark:bg-gray-700 rounded-xl p-4 sm:p-5 md:p-6',
          'overflow-hidden animate-pulse',
          className
        )}
      >
        {/* Skeleton accent line */}
        <div className="absolute inset-y-0 left-0 w-1 bg-gray-300 dark:bg-gray-600" />
        
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-[10px] bg-gray-300 dark:bg-gray-600" />

          <div className="flex-1">
            <div className="h-7 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2" />
          </div>
        </div>

        {/* Skeleton content */}
        {children && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-300 dark:bg-gray-600 rounded-lg" />
            ))}
          </div>
        )}
      </div>
    );
  }
  
  // Get icon URL or fallback to display name initials for text icons
  const iconUrl = game.iconUrl || game.displayName.substring(0, 2).toUpperCase();
  
  // Get external game styling if applicable
  const externalStyling = isExternalGame(game) ? getExternalGameStyling(game) : '';

  // D39: card zone is clickable when onCardClick provided and game is not under-construction
  const isCardClickable = !!onCardClick && game.contentAvailability !== 'under_construction';

  const sharedProps = {
    className: cn(
      'group relative bg-[#f8f8f8] dark:bg-gray-700 rounded-xl p-2.5 md:p-[clamp(8px,1vw,12px)] transition-all duration-200',
      'overflow-hidden',
      // D39: cursor-pointer and hover when card zone is clickable (logo/title/body → today's game)
      isCardClickable && 'cursor-pointer hover:shadow-md',
      'before:content-[""] before:absolute before:inset-y-0 before:left-0',
      'before:w-1 before:transition-all before:duration-200',
      // Apply Ultimate Experience styling for external games (square aspect ratio)
      externalStyling,
      className
    ),
    style: {
      '--sport-color': game.signatureColor,
      '--tw-shadow-color': `${game.signatureColor}10`
    } as React.CSSProperties
  };

  const content = (
    <>
      {/* Sport accent line */}
      <div
        className="absolute inset-y-0 left-0 w-1"
        style={{ backgroundColor: game.signatureColor }}
      />

      {/* Main layout - Consistent across all breakpoints:
          Icon on left, Title/Tagline + children (challenge stream) on right */}
      {showHeader && (
        <div className="flex flex-row gap-[clamp(6px,1.5vw,14px)] md:gap-[clamp(8px,1vw,14px)] items-start">
          {/* Icon - fixed square size */}
          <div className="flex-shrink-0">
            <div
              className={cn(
                "rounded-[10px] overflow-hidden flex items-center justify-center",
                // Mobile: larger icon to better align with title + stream height
                // Tablet (md): larger icon to match increased font sizes
                // Desktop (xl): original sizing
                game.isPublished && "w-[clamp(72px,19vw,96px)] h-[clamp(72px,19vw,96px)] md:w-[clamp(90px,12vw,115px)] md:h-[clamp(90px,12vw,115px)] xl:w-[clamp(56px,8vw,88px)] xl:h-[clamp(56px,8vw,88px)]",
                // Coming Soon games: fluid size (smaller than released but still visible)
                !game.isPublished && "w-[clamp(40px,11vw,60px)] h-[clamp(40px,11vw,60px)] md:w-[clamp(44px,6vw,68px)] md:h-[clamp(44px,6vw,68px)]"
              )}
            >
              {isImageIconUrl(iconUrl) ? (
                <img
                  src={iconUrl}
                  alt={game.displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-white font-bebas-neue text-[clamp(14px,3.5vw,20px)] md:text-[clamp(16px,1.2vw,28px)]"
                  style={{ backgroundColor: game.signatureColor }}
                >
                  {iconUrl}
                </div>
              )}
            </div>
          </div>

          {/* Right side: Title/tagline + children stacked */}
          <div className="flex-1 min-w-0">
            {/* Title and tagline - stacked on mobile for consistency, inline on tablet/desktop */}
            <div className="flex flex-col md:flex-row md:flex-wrap md:items-baseline gap-y-0 md:gap-x-[clamp(6px,0.6vw,10px)] mb-0.5 md:mb-1">
              <h3 className="font-bebas-neue text-[clamp(12px,3.2vw,16px)] md:text-[clamp(18px,2.2vw,28px)] xl:text-[clamp(16px,1.4vw,24px)] font-medium leading-tight tracking-[1px] md:tracking-[1.5px] uppercase text-foreground">
                {game.displayName}
              </h3>
              <p className="font-oswald text-[clamp(6px,1.5vw,8px)] md:text-[clamp(10px,1vw,14px)] xl:text-[clamp(9px,0.65vw,12px)] font-medium text-muted-foreground uppercase tracking-[0.3px] md:tracking-[0.5px] leading-tight">
                {game.tagline}
              </p>
            </div>

            {/* Children (challenge stream) below title */}
            {children}
          </div>
        </div>
      )}

      {/* Title only mode (no icon, used for specific layouts) */}
      {showTitleOnly && (
        <div className="mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-[clamp(8px,0.8vw,14px)]">
            <h3 className="font-bebas-neue text-[clamp(18px,1.6vw,30px)] font-medium leading-none tracking-[1.5px] uppercase text-foreground">
              {game.displayName}
            </h3>
            <p className="font-oswald text-[clamp(10px,0.8vw,14px)] font-medium text-muted-foreground uppercase tracking-[0.5px]">
              {game.tagline}
            </p>
          </div>
          {children}
        </div>
      )}

      {/* No header mode - just render children */}
      {!showHeader && !showTitleOnly && children}
    </>
  );

  return to ? (
    <Link to={to} {...sharedProps}>
      {content}
    </Link>
  ) : (
    // D39: onClick fires only when isCardClickable — under-construction wrapper stays inert
    <div
      {...sharedProps}
      onClick={isCardClickable ? onCardClick : undefined}
      role={isCardClickable ? 'link' : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      aria-label={isCardClickable ? `Play ${game.displayName}` : undefined}
      onKeyDown={isCardClickable ? (e) => {
        if (e.currentTarget !== e.target) return; // ignore bubbled events from children
        if (e.key === 'Enter') {
          e.preventDefault();
          onCardClick?.();
        }
      } : undefined}
    >
      {content}
    </div>
  );
}